import { action, computed, makeObservable, observable } from 'mobx'
import { CallCenterJobSummary } from 'call-center/models/CallCenterJobSummary'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import JobSearchQueryStringBuilder from 'jobs/infra/JobSearchQueryStringBuilder'
import { JobScopeOption } from 'jobs/dtos/job'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'

const SEARCH_LIMIT = 25

export class CallCenterJobHistoryViewModel {
  @observable
  clientUserPublicId: string | undefined = undefined

  constructor(private jobSearchStore: JobSearchStore) {
    makeObservable(this)
  }

  @computed
  get jobs() {
    return this.jobsScroller.items
  }

  @action.bound
  setUserAndLoadJobs(userPublicId: string): void {
    this.jobsScroller.reset(false)
    this.clientUserPublicId = userPublicId

    void this.jobsScroller.fetch()
  }

  @action.bound
  clear() {
    this.jobsScroller.reset(false)
    this.clientUserPublicId = undefined
  }

  jobsScroller = new InfiniteScrollProvider<CallCenterJobSummary>({
    limit: SEARCH_LIMIT,
    fetchItems: (paging, cancellationToken) =>
      this.fetchJobSummaries(
        paging,
        cancellationToken,
        this.clientUserPublicId,
      ),
  })

  private async fetchJobSummaries(
    paging: CursorPageQuery,
    cancellationToken: CancellationToken,
    clientPublicId?: string,
  ): Promise<CursorPaged<CallCenterJobSummary>> {
    if (!clientPublicId) {
      return CursorPaged.empty
    }

    try {
      const response = await this.jobSearchStore.searchJobs({
        filter: new JobSearchQueryStringBuilder()
          .withMemberIds([clientPublicId])
          .build(),
        $limit: paging.limit ?? SEARCH_LIMIT,
        scope: JobScopeOption.All,
        orderBy: 'date_created desc',
        after: paging.after ?? undefined,
      })

      cancellationToken.throwIfCancelled()

      const jobs = response.data
        .filter((job) => {
          const championClient = job.championClient
          if (championClient) {
            return championClient.id === clientPublicId
          }

          return job.members.some(
            (member) =>
              member.id === clientPublicId && member.type === 'CLIENT',
          )
        })
        .map((job) => ({
          id: job.id,
          name: job.displayName,
          total: job.total,
          workspace: job.workspace,
          status: job.status,
          jobScopeItems: job.jobScopeItems || [],
          dateCreated: job.dateCreated,
          legendId: job.legendId,
          legendVersion: job.legendVersion,
        }))

      return {
        data: jobs,
        cursor: response.cursor || null,
        total: response.total,
      }
    } catch (error) {
      if (cancellationToken.isCancelled) {
        return CursorPaged.empty
      }
      throw error
    }
  }
}
